import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLanguage } from "@/lib/lang";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import PeonyMark from "@/components/ui-brand/PeonyMark";
import { IMG } from "@/lib/assets";

/**
 * SlidesPage — editorial 4:5 social-media slides for Patrice.
 *
 * Use case: open the page on Desktop, navigate through slides, screenshot each
 * (or use the download/print button), upload to Instagram / Threads / LinkedIn.
 *
 * Each slide is rendered in true 4:5 ratio (1080×1350 design space) and scaled
 * elegantly to the viewport. Includes a navigation strip + slide number.
 *
 * Slide decks are defined as data structures so Patrice can add more later
 * without touching layout code.
 */

// ---------- Slide deck definitions ----------
const DECKS = {
  "clear-signal": {
    brand: "sgb",
    title: "CLEAR SIGNAL™",
    subtitle: "Was deine Profilanalyse wirklich bedeutet.",
    slides: [
      { kind: "cover", eyebrow: "Sichtbar Gut Bewertet", title: "CLEAR SIGNAL™", subtitle: "Die Profilanalyse, die wirklich zeigt, wo du stehst.", image: IMG.clearSignalHero },
      { kind: "manifesto", eyebrow: "01 — CLEAR", title: "Klarheit, die wirkt.", body: "Dein Profil wird analysiert. Deine Strategie wird klar — Buchstabe für Buchstabe." },
      { kind: "acronym", eyebrow: "02 — C·L·E·A·R", title: "CLEAR", items: [["C","CONTENT"],["L","LEADMAGNET"],["E","ENGAGEMENT"],["A","ALGORITHMUS"],["R","RESONANZ"]] },
      { kind: "acronym", eyebrow: "03 — S·I·G·N·A·L", title: "SIGNAL", items: [["S","SICHTBAR"],["I","INSPIRIEREND"],["G","GLAUBWÜRDIG"],["N","NAHBAR"],["A","ANZIEHEND"],["L","LOYAL"]] },
      { kind: "benefit", eyebrow: "04 — Content", title: "Du weißt, was wirklich ankommt.", body: "Nicht raten. Nicht hoffen. Wissen." },
      { kind: "benefit", eyebrow: "05 — Algorithmus", title: "Du arbeitest mit ihm — nicht gegen ihn.", body: "Der Algorithmus ist kein Mysterium. Er ist ein System mit Regeln." },
      { kind: "benefit", eyebrow: "06 — Anziehend", title: "Du ziehst die Richtigen an — ohne laut zu sein.", body: "Du brauchst keine Reichweite. Du brauchst Resonanz." },
      { kind: "cta", eyebrow: "07 — Jetzt", title: "Profilanalyse buchen.", body: "Eine ehrliche Bestandsaufnahme. Keine Buzzwords.", cta: "sichtbargutbewertet.de" },
    ],
  },
  "bewertungen": {
    brand: "sgb",
    title: "Google Bewertungen",
    subtitle: "Reputation aufräumen. Mit System.",
    slides: [
      { kind: "cover", eyebrow: "Sichtbar Gut Bewertet", title: "Google Bewertungen löschen.", subtitle: "Wenn eine einzelne Stimme deinen Ruf kostet — gibt es einen Weg.", image: IMG.sgbReputation },
      { kind: "manifesto", eyebrow: "01 — Realität", title: "Eine schlechte Bewertung kostet bis zu 22% deiner Kunden.", body: "Quelle: BrightLocal Local Consumer Review Survey." },
      { kind: "manifesto", eyebrow: "02 — Recht", title: "Nicht jede Bewertung ist erlaubt.", body: "Schmähkritik, Falschbehauptungen, fehlende Kundenbeziehung — das sind Angriffspunkte." },
      { kind: "benefit", eyebrow: "03 — Strukturiert", title: "Erst Prüfen.", body: "Wir analysieren die Bewertung juristisch — bevor wir handeln." },
      { kind: "benefit", eyebrow: "04 — Diskret", title: "Dann Beschwerde.", body: "Direkter Weg über die Google-Mechanismen, mit klaren Argumenten." },
      { kind: "benefit", eyebrow: "05 — Nachvollziehbar", title: "Du bekommst Reports.", body: "Jeder Schritt dokumentiert. Du verlierst nichts aus dem Blick." },
      { kind: "cta", eyebrow: "06 — Anfragen", title: "Lass uns dein Profil aufräumen.", body: "Erstgespräch unverbindlich.", cta: "sichtbargutbewertet.de" },
    ],
  },
  "webseiten": {
    brand: "sgb",
    title: "Webseiten",
    subtitle: "Die digitale Tür, durch die alles geht.",
    slides: [
      { kind: "cover", eyebrow: "Sichtbar Gut Bewertet", title: "Webseiten, die nicht laut sein müssen.", subtitle: "Editorial. Persönlich. Konvertierend.", image: IMG.sgbWebdesign },
      { kind: "manifesto", eyebrow: "01 — Realität", title: "75% deiner Kunden urteilen in 5 Sekunden über deine Webseite.", body: "Was sehen sie — wenn sie deine Seite aufrufen?" },
      { kind: "benefit", eyebrow: "02 — Editorial", title: "Wie ein Magazin. Nicht wie ein Baukasten.", body: "Asymmetrische Layouts. Bewegung. Stille. Atmosphäre." },
      { kind: "benefit", eyebrow: "03 — Persönlich", title: "Deine Stimme. Nicht meine Vorlage.", body: "Jede Webseite klingt nach der Person dahinter." },
      { kind: "benefit", eyebrow: "04 — Schnell", title: "Geladen in unter 2 Sekunden.", body: "Sichtbarkeit beginnt bei Performance." },
      { kind: "cta", eyebrow: "05 — Bauen wir?", title: "Lass uns deine digitale Tür entwerfen.", body: "Pakete oder maßgeschneidert.", cta: "sichtbargutbewertet.de" },
    ],
  },
  "sperrungen": {
    brand: "sgb",
    title: "Account-Sperrungen",
    subtitle: "Das muss nicht das Ende sein.",
    slides: [
      { kind: "cover", eyebrow: "Sichtbar Gut Bewertet", title: "Account gesperrt.", subtitle: "Das muss nicht das Ende sein.", image: "https://customer-assets.emergentagent.com/job_peony-redesign/artifacts/4o8mlmx8_Foto%2025-05-2026%2C%2015%2035%2057%20%282%29.jpg" },
      { kind: "manifesto", eyebrow: "01 — Realität", title: "TikTok weg. Instagram dicht. Und niemand erklärt dir warum.", body: "Eine Sperrung ist nie nur ein Klick." },
      { kind: "benefit", eyebrow: "02 — Erste Hilfe", title: "Kein Panik-Modus.", body: "Wir analysieren erst, was passiert ist, bevor wir Anträge stellen." },
      { kind: "benefit", eyebrow: "03 — DSA-Beschwerde", title: "Strukturiert. Diskret. Nachvollziehbar.", body: "Über die EU-Mechanismen — keine Schreierei, keine leeren Versprechen." },
      { kind: "benefit", eyebrow: "04 — Wiederaufbau", title: "Falls der Account verloren bleibt — bauen wir neu.", body: "Mit allem, was wir aus deiner Sperrung gelernt haben." },
      { kind: "cta", eyebrow: "05 — Schreib mir", title: "Lass uns reden.", body: "Vertraulich, ohne Verpflichtung.", cta: "sichtbargutbewertet.de" },
    ],
  },
  "profilanalyse": {
    brand: "sgb",
    title: "Profilanalyse",
    subtitle: "Wer du online bist — und wer du sein willst.",
    slides: [
      { kind: "cover", eyebrow: "Sichtbar Gut Bewertet", title: "Profilanalyse.", subtitle: "Eine ehrliche Bestandsaufnahme deiner digitalen Wirkung.", image: IMG.clearSignalHero },
      { kind: "manifesto", eyebrow: "01 — Frage", title: "Wenn jemand fremdes auf dein Profil klickt — was denkt er nach 8 Sekunden?", body: "Das ist die einzige Sekunde, die zählt." },
      { kind: "benefit", eyebrow: "02 — Methode", title: "Wir analysieren 11 Dimensionen.", body: "Content · Leadmagnet · Engagement · Algorithmus · Resonanz · Sichtbar · Inspirierend · Glaubwürdig · Nahbar · Anziehend · Loyal." },
      { kind: "benefit", eyebrow: "03 — Lieferung", title: "Du bekommst kein PDF. Du bekommst Klarheit.", body: "Ein 1:1 Gespräch + schriftliche Roadmap im Markendesign." },
      { kind: "benefit", eyebrow: "04 — Ergebnis", title: "Du weißt, was du als Nächstes tun musst.", body: "Drei klare Schritte. Priorisiert. Umsetzbar." },
      { kind: "cta", eyebrow: "05 — Buchen", title: "Lass dein Profil sprechen.", body: "Klarer, ehrlicher, anziehender.", cta: "sichtbargutbewertet.de" },
    ],
  },
  "patrice-studio": {
    brand: "studio",
    title: "patrice.studio",
    subtitle: "Ein digitales Atelier.",
    slides: [
      { kind: "cover", eyebrow: "patrice.studio", title: "Ein digitales Atelier.", subtitle: "Mehrere Disziplinen. Eine Handschrift.", image: IMG.studioHeader },
      { kind: "manifesto", eyebrow: "01 — Manifest", title: "Wow — ohne laut zu sein.", body: "Persönlich, präzise, immer mit Substanz." },
      { kind: "benefit", eyebrow: "02 — Werte", title: "Klar · Sichtbar · Authentisch · Präzise · Kreativ.", body: "Fünf Wörter. Eine Haltung." },
      { kind: "benefit", eyebrow: "03 — Universum", title: "Sichtbar Gut Bewertet.", body: "Wo Reputation, Webdesign und Profilanalyse wohnen." },
      { kind: "benefit", eyebrow: "04 — Erlebnisräume", title: "Fünf Räume. Eine Handschrift.", body: "Fotografie · Kunst · Klang · Kreativ · Atelier." },
      { kind: "cta", eyebrow: "05 — Komm rein", title: "patrice.studio", body: "Das Zuhause meiner Marke.", cta: "patrice.studio" },
    ],
  },
};

// ---------- Slide renderer (true 1080×1350 design surface) ----------
const Slide = ({ slide, index, total, deckTitle, brand }) => {
  const isSGB = brand === "sgb";
  const bg = isSGB ? "#28001B" : "#FAF7F2";
  const fg = isSGB ? "#FAF7F2" : "#28001B";
  const accent = "#FF7100";

  const Cover = () => (
    <div className="relative w-full h-full overflow-hidden" style={{ background: bg }}>
      {slide.image && (
        <img
          src={slide.image}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.55 }}
        />
      )}
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(180deg, ${bg}88 0%, ${bg}EE 100%)` }}
      />
      <div className="relative z-10 h-full flex flex-col justify-between p-16">
        <div>
          <p className="font-mono uppercase" style={{ fontSize: 18, letterSpacing: "0.32em", color: accent }}>
            {slide.eyebrow}
          </p>
        </div>
        <div>
          <h2 className="font-display leading-[0.96]" style={{ fontSize: 92, color: fg, fontWeight: 300, letterSpacing: "-0.015em" }}>
            <span className="italic-serif">{slide.title.split(" ")[0]}</span>
            {slide.title.includes(" ") && <> {slide.title.split(" ").slice(1).join(" ")}</>}
          </h2>
          <p className="mt-8 font-light max-w-md" style={{ fontSize: 26, color: fg, opacity: 0.92, lineHeight: 1.3 }}>
            {slide.subtitle}
          </p>
        </div>
        <div className="flex justify-between items-end" style={{ fontSize: 14, color: fg, opacity: 0.75 }}>
          <span className="font-mono uppercase" style={{ letterSpacing: "0.32em" }}>
            {deckTitle}
          </span>
          <span className="font-mono" style={{ letterSpacing: "0.32em" }}>
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
        </div>
      </div>
    </div>
  );

  const Manifesto = () => (
    <div className="relative w-full h-full p-16 flex flex-col justify-center" style={{ background: bg, color: fg }}>
      <p className="font-mono uppercase mb-10" style={{ fontSize: 16, letterSpacing: "0.32em", color: accent }}>
        {slide.eyebrow}
      </p>
      <h2 className="font-display leading-[1.02] max-w-3xl" style={{ fontSize: 64, fontWeight: 300, letterSpacing: "-0.01em" }}>
        <span className="italic-serif">{slide.title}</span>
      </h2>
      {slide.body && (
        <p className="mt-10 font-light max-w-2xl" style={{ fontSize: 22, opacity: 0.92, lineHeight: 1.5 }}>
          {slide.body}
        </p>
      )}
      <div className="absolute bottom-16 right-16 font-mono" style={{ fontSize: 14, letterSpacing: "0.32em", opacity: 0.6 }}>
        {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </div>
    </div>
  );

  const Acronym = () => (
    <div className="relative w-full h-full p-16 flex flex-col justify-center" style={{ background: bg, color: fg }}>
      <p className="font-mono uppercase mb-10" style={{ fontSize: 16, letterSpacing: "0.32em", color: accent }}>
        {slide.eyebrow}
      </p>
      <div className="space-y-3">
        {slide.items.map(([letter, word]) => (
          <p key={letter} className="font-display leading-[0.95]" style={{ fontSize: 78, fontWeight: 400 }}>
            <span style={{ color: accent }}>{letter}</span>
            <span>{word.slice(1)}</span>
          </p>
        ))}
      </div>
      <div className="absolute bottom-16 right-16 font-mono" style={{ fontSize: 14, letterSpacing: "0.32em", opacity: 0.6 }}>
        {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </div>
    </div>
  );

  const Benefit = () => (
    <div className="relative w-full h-full p-16 flex flex-col justify-end" style={{ background: bg, color: fg }}>
      <p className="font-mono uppercase" style={{ fontSize: 16, letterSpacing: "0.32em", color: accent }}>
        {slide.eyebrow}
      </p>
      <h2 className="font-display mt-8 leading-[1.0]" style={{ fontSize: 56, fontWeight: 400, letterSpacing: "-0.005em" }}>
        <span className="italic-serif">{slide.title.split(" ").slice(0, 2).join(" ")}</span>
        {slide.title.split(" ").length > 2 && <> {slide.title.split(" ").slice(2).join(" ")}</>}
      </h2>
      {slide.body && (
        <p className="mt-8 font-light" style={{ fontSize: 24, opacity: 0.9, lineHeight: 1.45 }}>
          {slide.body}
        </p>
      )}
      <div className="absolute top-16 right-16 font-mono" style={{ fontSize: 14, letterSpacing: "0.32em", opacity: 0.6 }}>
        {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </div>
    </div>
  );

  const CTA = () => (
    <div className="relative w-full h-full p-16 flex flex-col justify-center items-start" style={{ background: accent, color: "#28001B" }}>
      <p className="font-mono uppercase" style={{ fontSize: 16, letterSpacing: "0.32em", opacity: 0.7 }}>
        {slide.eyebrow}
      </p>
      <h2 className="font-display mt-10 leading-[0.96]" style={{ fontSize: 88, fontWeight: 400, letterSpacing: "-0.015em" }}>
        <span className="italic-serif">{slide.title}</span>
      </h2>
      {slide.body && (
        <p className="mt-10 font-light max-w-2xl" style={{ fontSize: 26, lineHeight: 1.4 }}>
          {slide.body}
        </p>
      )}
      <p className="mt-14 font-mono uppercase" style={{ fontSize: 22, letterSpacing: "0.24em" }}>
        {slide.cta} ↗
      </p>
    </div>
  );

  if (slide.kind === "cover") return <Cover />;
  if (slide.kind === "manifesto") return <Manifesto />;
  if (slide.kind === "acronym") return <Acronym />;
  if (slide.kind === "benefit") return <Benefit />;
  if (slide.kind === "cta") return <CTA />;
  return null;
};

const DeckIndex = () => {
  const { lang } = useLanguage();
  useDocumentMeta({
    title: "Slides · Editorial Carousels für Social Media · patrice.studio",
    description: "Vorgefertigte Slide-Decks im Markendesign für Instagram, Threads, LinkedIn.",
  });
  return (
    <section className="relative px-6 md:px-12 lg:px-24 pt-12 md:pt-20 pb-24 overflow-hidden">
      <PeonyMark size={560} opacity={0.05} className="absolute -right-32 -top-24 pointer-events-none" />
      <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere">
        Social · 4:5 Slides
      </p>
      <h1 className="font-display text-[clamp(2.4rem,6vw,5.4rem)] leading-[0.96] mt-8">
        <span className="italic-serif text-atmosphere">Slide-Decks</span> für Insta & Co.
      </h1>
      <p className="body-lux mt-10 max-w-2xl">
        {lang === "de"
          ? "Wähle ein Thema. Jeder Slide ist im 4:5 Instagram-Format, im Markendesign. Klick auf einen Slide → Screenshot oder Bildschirmaufnahme — fertig für deinen Feed."
          : "Pick a topic. Each slide is 4:5 Instagram-ready, in brand. Click a slide → screenshot or screen-record — ready for your feed."}
      </p>
      <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(DECKS).map(([key, deck]) => (
          <Link
            key={key}
            to={`/slides/${key}`}
            data-testid={`slides-deck-${key}`}
            className="group block border border-warmth/30 hover:border-depth p-8 transition-colors"
          >
            <p className="font-mono text-[0.58rem] tracking-[0.32em] uppercase text-atmosphere">
              {deck.brand === "sgb" ? "Sichtbar Gut Bewertet" : "patrice.studio"}
            </p>
            <h2 className="font-display text-2xl md:text-3xl mt-4 text-depth group-hover:text-signal transition-colors">
              <span className="italic-serif">{deck.title}</span>
            </h2>
            <p className="mt-4 font-light text-depth/80">{deck.subtitle}</p>
            <p className="mt-8 font-mono text-[0.58rem] tracking-[0.32em] uppercase text-depth group-hover:text-signal transition-colors">
              {deck.slides.length} Slides ↗
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
};

const DeckViewer = () => {
  const { deck: deckKey } = useParams();
  const deck = DECKS[deckKey];
  const [active, setActive] = useState(0);
  useDocumentMeta({
    title: `Slides · ${deck?.title || "Deck"} · patrice.studio`,
  });

  if (!deck) {
    return (
      <section className="p-12">
        <p>Deck nicht gefunden.</p>
        <Link to="/slides" className="underline">Zurück zur Übersicht</Link>
      </section>
    );
  }

  return (
    <section className="px-6 md:px-12 py-12 md:py-16 bg-warmth/10 min-h-screen">
      <div className="flex items-center justify-between mb-8">
        <Link to="/slides" className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-depth hover:text-signal">
          ← Alle Decks
        </Link>
        <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere">
          {deck.title} · {String(active + 1).padStart(2, "0")} / {String(deck.slides.length).padStart(2, "0")}
        </p>
        <button
          type="button"
          onClick={() => window.print()}
          className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-depth hover:text-signal"
        >
          Drucken / PDF ↗
        </button>
      </div>

      {/* Active slide — 4:5 ratio, scaled */}
      <div className="flex justify-center mb-10">
        <div
          data-testid={`slides-slide-${active}`}
          style={{
            width: "min(540px, 92vw)",
            aspectRatio: "4 / 5",
          }}
          className="shadow-2xl border border-depth/20 overflow-hidden"
        >
          <Slide
            slide={deck.slides[active]}
            index={active}
            total={deck.slides.length}
            deckTitle={deck.title}
            brand={deck.brand}
          />
        </div>
      </div>

      {/* Thumbnail strip */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {deck.slides.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`slides-thumb-${i}`}
              className={`shrink-0 border-2 transition-colors ${
                i === active ? "border-signal" : "border-transparent hover:border-warmth"
              }`}
              style={{ width: 96, aspectRatio: "4 / 5", overflow: "hidden" }}
            >
              <div style={{ transform: "scale(0.09)", transformOrigin: "top left", width: 1080, height: 1350 }}>
                <Slide slide={s} index={i} total={deck.slides.length} deckTitle={deck.title} brand={deck.brand} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export const SlidesIndexPage = DeckIndex;
export const SlidesDeckPage = DeckViewer;

export default DeckIndex;
